import type { JournalPost } from "./types";

/** v2 / review article shell hint stored on `blog_posts.meta_description.blog_layout`. */
export type PostBlogLayout =
  | "looks_products_roundup"
  | "transcript_longform_screenshots"
  | "review_longform_screenshots";

export function resolvePostBlogLayout(
  post: { metaDescription?: unknown; platform?: unknown } | null | undefined,
): PostBlogLayout | null {
  if (!post) return null;
  const candidates = [post.metaDescription, post.platform];
  for (const candidate of candidates) {
    let rec: Record<string, unknown> | null = null;
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      rec = candidate as Record<string, unknown>;
    } else if (typeof candidate === "string") {
      try {
        const parsed = JSON.parse(candidate);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          rec = parsed as Record<string, unknown>;
        }
      } catch {
        /* ignore */
      }
    }
    const layout = String(rec?.blog_layout || "").trim();
    if (layout === "looks_products_roundup") return "looks_products_roundup";
    if (layout === "transcript_longform_screenshots") {
      return "transcript_longform_screenshots";
    }
    if (layout === "review_longform_screenshots") {
      return "review_longform_screenshots";
    }
  }
  return null;
}

function relatedBlogMetaRecord(post: JournalPost): Record<string, unknown> | null {
  const candidates = [post.metaDescription, post.platform];
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === "string") {
      const text = candidate.trim();
      if (!text) continue;
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return parsed as Record<string, unknown>;
        }
      } catch {
        continue;
      }
    }
    if (typeof candidate === "object" && !Array.isArray(candidate)) {
      return candidate as Record<string, unknown>;
    }
  }
  return null;
}

function relatedIdList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? "").trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    const text = value.trim();
    if (!text) return [];
    if (text.startsWith("[") && text.endsWith("]")) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          return parsed.map((item) => String(item ?? "").trim()).filter(Boolean);
        }
      } catch {
        // Fall through to delimiter-based splitting.
      }
    }
    return text
      .split(/[\n,|]/g)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (value != null) {
    const single = String(value).trim();
    return single ? [single] : [];
  }
  return [];
}

function collectRelatedIds(
  meta: Record<string, unknown> | null,
  keys: string[],
): string[] {
  if (!meta) return [];
  const out: string[] = [];
  for (const key of keys) out.push(...relatedIdList(meta[key]));
  return Array.from(new Set(out));
}

/** Resolve content + product post IDs linked from a journal article. */
export function resolveRelatedBlogModules(post: JournalPost): {
  contentPostIds: string[];
  productPostIds: string[];
} {
  const meta = relatedBlogMetaRecord(post);
  const contentKeys = [
    "source_post_id",
    "sourcePostId",
    "post_id",
    "postId",
    "post_ids",
    "postIds",
    "content_post_id",
    "contentPostId",
    "content_post_ids",
    "contentPostIds",
    "content_ids",
    "contentIds",
    "linked_post_ids",
    "linkedPostIds",
  ];
  const productKeys = [
    "product_id",
    "productId",
    "product_ids",
    "productIds",
    "linked_product_ids",
    "linkedProductIds",
    "shop_post_id",
    "shopPostId",
    "shop_post_ids",
    "shopPostIds",
  ];
  const contentPostIds = Array.from(
    new Set(
      [
        ...(post.sourcePostId ? [String(post.sourcePostId).trim()] : []),
        ...collectRelatedIds(meta, contentKeys),
      ].filter(Boolean),
    ),
  );
  // PRODUCT rows are keyed by the content `post_id` they were tagged on —
  // include source/content posts so shoppable items from the generating post appear.
  const productPostIds = Array.from(
    new Set(
      [...contentPostIds, ...collectRelatedIds(meta, productKeys)].filter(Boolean),
    ),
  );
  return { contentPostIds, productPostIds };
}
